<div class="vr-edit">
  <header class="vr-header">
    <div class="vr-header-row">
      <button type="button" class="btn-back" @click="goBack">← 返回后台</button>
      <button
        v-if="hallId != null"
        type="button"
        class="btn-preview"
        @click="openPanoramaPreview"
      >
        预览全景
      </button>
    </div>
    <h1>VR 全景编辑</h1>
    <p class="vr-meta">
      祠堂 ID：<strong>{{ hallId != null ? hallId : '—' }}</strong>
      · 先上传全景场景，再为每个场景配置热点（yaw/pitch 决定标签位置）
    </p>
  </header>

  <div v-if="hallId == null" class="banner-error">无效的祠堂 ID，请从祠堂管理进入。</div>

  <div v-else class="vr-content">
    <section class="card">
      <div class="card-head">
        <h2>全景场景</h2>
        <div class="card-tools">
          <label class="chk">
            <input v-model="sceneOnlyActive" type="checkbox" />
            仅启用 state=1
          </label>
          <button type="button" class="btn" :disabled="sceneLoading" @click="loadScenes">
            刷新
          </button>
          <button type="button" class="btn primary" @click="openNewScene">新建场景</button>
        </div>
      </div>
      <p v-if="sceneMsg" class="msg-error">{{ sceneMsg }}</p>
      <details class="field-legend">
        <summary>全景场景 · 列表字段说明</summary>
        <dl>
          <dt>ID</dt>
          <dd>场景唯一编号，热点跳转时通过 targetSceneId 引用。</dd>
          <dt>名称 name</dt>
          <dd>展示用场景名，如「前厅」「惇叙堂」，出现在预览页底部场景条与后台列表。</dd>
          <dt>imageUrl</dt>
          <dd>360° 等距柱状全景图（建议 2:1 比例）的 COS 地址，预览页加载的主贴图。</dd>
          <dt>thumbnailUrl</dt>
          <dd>底部场景切换条的小图；未配置时预览自动回退使用全景图。</dd>
          <dt>sortOrder</dt>
          <dd>排序权重，数字越小越靠前，决定场景条从左到右的默认顺序。</dd>
          <dt>northOffsetDeg</dt>
          <dd>全景水平朝向校正（度），用于不同拍摄朝向与热点坐标对齐；预留扩展。</dd>
          <dt>state</dt>
          <dd>1 启用 / 0 停用；勾选「仅启用」时只加载 state=1 的场景。</dd>
        </dl>
      </details>
      <div class="table-wrap">
        <table class="tbl">
          <thead>
            <tr>
              <th>ID<span class="col-hint">唯一编号</span></th>
              <th>名称<span class="col-hint">场景条显示名</span></th>
              <th>imageUrl<span class="col-hint">全景主图</span></th>
              <th>thumbnailUrl<span class="col-hint">场景条缩略图</span></th>
              <th>sortOrder<span class="col-hint">排序</span></th>
              <th>northOffsetDeg<span class="col-hint">朝向校正</span></th>
              <th>state<span class="col-hint">启用状态</span></th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!sceneLoading && scenes.length === 0">
              <td colspan="8" class="empty">暂无场景，请先新建</td>
            </tr>
            <tr v-for="s in scenes" :key="s.id">
              <td>{{ s.id }}</td>
              <td>{{ s.name != null ? s.name : '—' }}</td>
              <td class="mono">{{ truncate(s.imageUrl, 36) }}</td>
              <td class="mono">{{ truncate(s.thumbnailUrl, 28) }}</td>
              <td>{{ s.sortOrder != null ? s.sortOrder : '—' }}</td>
              <td>{{ s.northOffsetDeg != null ? s.northOffsetDeg : '—' }}</td>
              <td>{{ s.state != null ? s.state : '—' }}</td>
              <td class="td-actions">
                <button type="button" class="link-btn" @click="openEditScene(s)">编辑</button>
                <button type="button" class="link-btn danger" @click="doDeleteScene(s)">删除</button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="sceneLoading" class="overlay-loading">加载中…</div>
      </div>

      <div v-if="scenePanelOpen" class="form-panel">
        <h3>{{ sceneForm.id ? '编辑场景' : '新建场景' }}</h3>
        <div class="form-grid">
          <label>
            <span>名称 name</span>
            <p class="field-hint">场景在预览底部场景条和后台列表中显示的中文名，如「前厅」「天井」；可不填，预览会显示「场景 N」。</p>
            <input v-model="sceneForm.name" type="text" placeholder="如：前厅、惇叙堂" />
          </label>
          <label class="full">
            <span>全景图 imageUrl <em>*</em></span>
            <p class="field-hint">必填。上传 360° 等距柱状全景图（常见 2:1，如 4096×2048），保存后 COS 链接只读。用户进入该场景时加载此图作为球体内壁贴图。</p>
            <div class="url-upload-row">
              <input
                class="readonly-url"
                type="text"
                readonly
                :value="sceneForm.imageUrl"
                placeholder="请点击上传选择全景图"
              />
              <button
                type="button"
                class="btn upload-inline"
                :disabled="uploadingPanorama || sceneLoading"
                @click="triggerPanoramaPick"
              >
                {{ uploadingPanorama ? '上传中…' : '上传全景图' }}
              </button>
              <input
                ref="panoramaFileRef"
                class="file-input-hidden"
                type="file"
                accept="image/*"
                @change="handlePanoramaFile"
              />
            </div>
          </label>
          <label class="full">
            <span>缩略图 thumbnailUrl</span>
            <p class="field-hint">可选。预览页底部横向场景条的小预览图；建议用同场景截图或裁剪图。未上传时预览自动使用全景图。</p>
            <div class="url-upload-row">
              <input
                class="readonly-url"
                type="text"
                readonly
                :value="sceneForm.thumbnailUrl"
                placeholder="可选，点击上传缩略图"
              />
              <button
                type="button"
                class="btn upload-inline"
                :disabled="uploadingThumb || sceneLoading"
                @click="triggerThumbPick"
              >
                {{ uploadingThumb ? '上传中…' : '上传缩略图' }}
              </button>
              <input
                ref="thumbFileRef"
                class="file-input-hidden"
                type="file"
                accept="image/*"
                @change="handleThumbFile"
              />
            </div>
          </label>
          <label>
            <span>sortOrder 排序</span>
            <p class="field-hint">数字越小越靠前。控制预览底部场景条的默认顺序及 API 列表排序，相同值按 ID 排列。</p>
            <input v-model.number="sceneForm.sortOrder" type="number" placeholder="0" />
          </label>
          <label>
            <span>northOffsetDeg 北向偏移</span>
            <p class="field-hint">全景图水平零点校正（单位：度）。当热点 yaw 与建筑实际方向对不上时，可整体旋转坐标系；留空表示不校正。</p>
            <input v-model="sceneForm.northOffsetDeg" type="text" placeholder="如 90 或留空" />
          </label>
          <label>
            <span>state 状态</span>
            <p class="field-hint">1 启用：预览与 API（only_active=1）可见；0 停用：隐藏但数据保留，删除场景会级联删除其热点。</p>
            <select v-model.number="sceneForm.state">
              <option :value="1">1 启用</option>
              <option :value="0">0 停用</option>
            </select>
          </label>
          <label class="full">
            <span>defaultConfig 默认视角（JSON）</span>
            <p class="field-hint">可选。存入数据库，用于配置进入本场景时的默认视线等，例如：{"startYawDeg":90,"startPitchDeg":0,"fovDeg":65}。yaw 为水平角（0～360），pitch 为俯仰（负向下、正向上）。</p>
            <textarea v-model="sceneForm.defaultConfigText" rows="4" placeholder='{"startYawDeg":90,"startPitchDeg":0}'></textarea>
          </label>
          <label class="full">
            <span>extraConfig 扩展配置（JSON）</span>
            <p class="field-hint">可选。预留扩展字段，可存放与预览无关的业务参数；须为合法 JSON 对象。</p>
            <textarea v-model="sceneForm.extraConfigText" rows="3" placeholder="{}"></textarea>
          </label>
        </div>
        <div class="form-actions">
          <button type="button" class="btn primary" :disabled="sceneLoading" @click="submitScene">
            保存
          </button>
          <button type="button" class="btn" @click="cancelSceneForm">取消</button>
        </div>
      </div>
    </section>

    <section class="card">
      <div class="card-head">
        <h2>热点</h2>
        <div class="card-tools">
          <label class="sel-label">
            当前场景
            <select v-model.number="hotspotSceneId" class="scene-select">
              <option v-if="scenes.length === 0" :value="null" disabled>暂无场景</option>
              <option v-for="s in scenes" :key="'hs-' + s.id" :value="s.id">
                {{ s.id }} · {{ s.name || '未命名' }}
              </option>
            </select>
          </label>
          <label class="chk">
            <input v-model="hotspotOnlyActive" type="checkbox" />
            仅启用
          </label>
          <button type="button" class="btn" :disabled="hotspotLoading" @click="loadHotspots">
            刷新热点
          </button>
          <button type="button" class="btn primary" @click="openNewHotspot">新建热点</button>
        </div>
      </div>
      <p v-if="hotspotMsg" class="msg-error">{{ hotspotMsg }}</p>
      <details class="field-legend">
        <summary>热点 · 列表字段说明</summary>
        <dl>
          <dt>hotspotType</dt>
          <dd>link_scene：点击跳转到同祠堂另一场景；image：点击弹出图片浮层，不切换场景。</dd>
          <dt>label</dt>
          <dd>热点在全景画面上显示的文字，如「惇叙堂」「查看匾额」。</dd>
          <dt>yawDeg / pitchDeg</dt>
          <dd>热点在球面上的水平角与俯仰角（度），决定标签钉在建筑上的位置；可在预览中试角度后回填。</dd>
          <dt>direction</dt>
          <dd>仅 link_scene：引路箭头方向 straight 直行 / left 左转 / right 右转；image 类型为空。</dd>
          <dt>target / 图</dt>
          <dd>跳转类显示目标场景 ID；图片类显示缩略预览。</dd>
          <dt>state</dt>
          <dd>1 启用 / 0 停用；还需 visible、enabled 为真时预览才显示并可点。</dd>
        </dl>
      </details>
      <div class="table-wrap">
        <table class="tbl">
          <thead>
            <tr>
              <th>ID<span class="col-hint">唯一编号</span></th>
              <th>类型<span class="col-hint">跳转/图片</span></th>
              <th>label<span class="col-hint">显示文字</span></th>
              <th>yaw<span class="col-hint">水平角</span></th>
              <th>pitch<span class="col-hint">俯仰角</span></th>
              <th>direction<span class="col-hint">引路方向</span></th>
              <th>target / 图<span class="col-hint">目标场景或图</span></th>
              <th>state<span class="col-hint">启用状态</span></th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="hotspotSceneId == null">
              <td colspan="9" class="empty">请先创建并选择场景</td>
            </tr>
            <tr v-else-if="!hotspotLoading && hotspots.length === 0">
              <td colspan="9" class="empty">该场景下暂无热点</td>
            </tr>
            <template v-else>
              <tr v-for="h in hotspots" :key="h.id">
                <td>{{ h.id }}</td>
                <td>{{ h.hotspotType }}</td>
                <td>{{ h.label != null ? h.label : '—' }}</td>
                <td>{{ h.yawDeg != null ? h.yawDeg : '—' }}</td>
                <td>{{ h.pitchDeg != null ? h.pitchDeg : '—' }}</td>
                <td>{{ resolveHotspotDirection(h) ? formatDirectionLabel(resolveHotspotDirection(h)) : '—' }}</td>
                <td>
                  <span v-if="h.hotspotType === 'image'">
                    <img
                      v-if="h.imageUrl"
                      :src="h.imageUrl"
                      class="hotspot-list-thumb"
                      alt=""
                    />
                    <span v-else>—</span>
                  </span>
                  <span v-else>→ 场景 {{ h.targetSceneId }}</span>
                </td>
                <td>{{ h.state != null ? h.state : '—' }}</td>
                <td class="td-actions">
                  <button type="button" class="link-btn" @click="openEditHotspot(h)">编辑</button>
                  <button type="button" class="link-btn danger" @click="doDeleteHotspot(h)">删除</button>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <div v-if="hotspotLoading" class="overlay-loading">加载中…</div>
      </div>

      <div v-if="hotspotPanelOpen" class="form-panel">
        <h3>{{ hotspotForm.id ? '编辑热点' : '新建热点' }}</h3>
        <div class="form-grid">
          <label>
            <span>hotspotType 热点类型 <em>*</em></span>
            <p class="field-hint">link_scene：720 云式引路标，点击切换到目标场景；image：在当前全景上弹出图片说明，不离开本场景。</p>
            <select v-model="hotspotForm.hotspotType">
              <option value="link_scene">link_scene（跳转场景）</option>
              <option value="image">image（查看图片）</option>
            </select>
          </label>
          <label>
            <span>label 显示文字</span>
            <p class="field-hint">钉在全景画面上的按钮文字，如「惇叙堂」「天井」「查看族谱」。用户转头时标签随视角移动。</p>
            <input v-model="hotspotForm.label" type="text" placeholder="如：惇叙堂" />
          </label>
          <label>
            <span>yawDeg 水平角</span>
            <p class="field-hint">热点左右位置（0～360°）。0° 为默认前方基准，增大向右转。在预览页对准目标后，把 URL 或调试值抄回此处。</p>
            <input v-model="hotspotForm.yawDeg" type="text" placeholder="如 120" />
          </label>
          <label>
            <span>pitchDeg 俯仰角</span>
            <p class="field-hint">热点上下位置。0 为水平视线，负值向下（地面/门洞），正值向上（屋檐/匾额）。预览限制约 -45°～45°。</p>
            <input v-model="hotspotForm.pitchDeg" type="text" placeholder="如 -5 或 10" />
          </label>
          <label>
            <span>icon 自定义图标</span>
            <p class="field-hint">可选。热点图标图片 URL，用于替换默认圆角按钮样式；留空则使用预览页内置样式。</p>
            <input v-model="hotspotForm.icon" type="text" placeholder="https://… 可选" />
          </label>
          <label>
            <span>sortOrder 排序</span>
            <p class="field-hint">后台列表与 API 返回顺序，数字越小越靠前；不影响球面上的空间位置。</p>
            <input v-model.number="hotspotForm.sortOrder" type="number" placeholder="0" />
          </label>
          <label>
            <span>zIndex 叠放层级</span>
            <p class="field-hint">多个热点投影到屏幕同一区域时，数值越大越显示在上层（预留，当前预览以 DOM 顺序为主）。</p>
            <input v-model.number="hotspotForm.zIndex" type="number" placeholder="0" />
          </label>
          <label>
            <span>visible 是否显示</span>
            <p class="field-hint">勾选后预览中渲染该热点；取消勾选则隐藏（数据仍保留）。</p>
            <label class="chk-inline">
              <input v-model="hotspotForm.visible" type="checkbox" />
              在预览中显示
            </label>
          </label>
          <label>
            <span>enabled 是否可点</span>
            <p class="field-hint">勾选后用户可点击跳转或看图；取消勾选则仅展示（当前预览会整体隐藏不可点热点）。</p>
            <label class="chk-inline">
              <input v-model="hotspotForm.enabled" type="checkbox" />
              允许点击交互
            </label>
          </label>
          <label>
            <span>state 状态</span>
            <p class="field-hint">1 启用 / 0 停用。勾选「仅启用」时 API 不返回 state=0 的记录；软删除请用删除操作。</p>
            <select v-model.number="hotspotForm.state">
              <option :value="1">1 启用</option>
              <option :value="0">0 停用</option>
            </select>
          </label>

          <div
            v-if="hotspotForm.hotspotType === 'link_scene'"
            class="hotspot-branch"
          >
            <label class="full">
              <span>targetSceneId 目标场景 <em>*</em></span>
              <p class="field-hint">点击热点后要进入的同祠堂场景 ID（不能选当前场景）。对应预览底部场景条中的另一张全景。</p>
              <select v-model="hotspotForm.targetSceneId" class="full-input">
                <option value="">请选择</option>
                <option v-for="os in otherScenes" :key="'ts-' + os.id" :value="String(os.id)">
                  {{ os.id }} · {{ os.name || '未命名' }}
                </option>
              </select>
            </label>
            <label class="full">
              <span>direction 引路方向 <em>*</em></span>
              <p class="field-hint">仅 link_scene 必填，保存到接口字段 direction。straight 直行箭头；left / right 为 L 形转弯路径。image 类型无需填写，保存时后端自动置空。</p>
              <div class="guide-type-options">
                <label
                  v-for="option in [
                    { value: 'straight', label: '直行 straight', symbol: '↑' },
                    { value: 'left', label: '左转 left', symbol: '↰' },
                    { value: 'right', label: '右转 right', symbol: '↱' }
                  ]"
                  :key="option.value"
                  class="guide-type-option"
                  :class="{ 'is-active': hotspotForm.direction === option.value }"
                >
                  <input v-model="hotspotForm.direction" type="radio" :value="option.value" />
                  <strong>{{ option.symbol }}</strong>
                  <span>{{ option.label }}</span>
                </label>
              </div>
            </label>
            <label>
              <span>entryYawDeg 入场水平角</span>
              <p class="field-hint">跳入目标场景后镜头朝哪看（水平角，度）。留空则用目标场景 defaultConfig 或预览默认 start_yaw。</p>
              <input v-model="hotspotForm.entryYawDeg" type="text" placeholder="如 90" />
            </label>
            <label>
              <span>entryPitchDeg 入场俯仰角</span>
              <p class="field-hint">跳入目标场景后的上下视角（度）。留空则用场景默认俯仰；通常略向下对准地面或门洞。</p>
              <input v-model="hotspotForm.entryPitchDeg" type="text" placeholder="如 0" />
            </label>
            <label class="full">
              <span>transition 切换效果</span>
              <p class="field-hint">可选。场景切换动画标识（如 fade、zoom），预留字段；当前预览为即时换图。</p>
              <input v-model="hotspotForm.transition" type="text" placeholder="可选，如 fade" />
            </label>
          </div>

          <div v-else class="hotspot-branch">
            <label class="full">
              <span>imageUrl 热点图片 <em>*</em>（新建必填）</span>
              <p class="field-hint">用户点击 image 热点后，在全景上方浮层展示的大图（族谱、匾额、文物照片等）。上传到 COS，链接只读。</p>
              <div class="image-upload-field">
                <div class="image-preview image-preview-main">
                  <img v-if="hotspotForm.imageUrl" :src="hotspotForm.imageUrl" alt="热点图片" />
                  <span v-else>暂无图片，请点击上传</span>
                </div>
                <button
                  type="button"
                  class="btn upload-inline"
                  :disabled="uploadingHotspotImage || hotspotLoading"
                  @click="triggerHotspotImagePick"
                >
                  {{ uploadingHotspotImage ? '上传中…' : '上传图片' }}
                </button>
                <input
                  ref="hotspotImageFileRef"
                  class="file-input-hidden"
                  type="file"
                  accept="image/*"
                  @change="handleHotspotImageFile"
                />
              </div>
            </label>
            <label class="full">
              <span>thumbUrl 缩略图</span>
              <p class="field-hint">可选。后台列表小预览或将来用作热点入口图标；未上传不影响浮层大图展示。</p>
              <div class="image-upload-field">
                <div class="image-preview image-preview-thumb">
                  <img v-if="hotspotForm.thumbUrl" :src="hotspotForm.thumbUrl" alt="缩略图" />
                  <span v-else>暂无缩略图</span>
                </div>
                <button
                  type="button"
                  class="btn upload-inline"
                  :disabled="uploadingHotspotThumb || hotspotLoading"
                  @click="triggerHotspotThumbPick"
                >
                  {{ uploadingHotspotThumb ? '上传中…' : '上传缩略图' }}
                </button>
                <input
                  ref="hotspotThumbFileRef"
                  class="file-input-hidden"
                  type="file"
                  accept="image/*"
                  @change="handleHotspotThumbFile"
                />
              </div>
            </label>
            <label>
              <span>title 浮层标题</span>
              <p class="field-hint">图片浮层顶部标题，如「李氏宗祠匾额」；留空则不显示标题行。</p>
              <input v-model="hotspotForm.title" type="text" placeholder="浮层标题" />
            </label>
            <label>
              <span>caption 浮层说明</span>
              <p class="field-hint">图片下方补充说明文字，如年代、出处；留空则不显示说明行。</p>
              <input v-model="hotspotForm.caption" type="text" placeholder="图片说明" />
            </label>
            <label>
              <span>openMode 打开方式</span>
              <p class="field-hint">可选。图片展示方式标识，如 modal（模态浮层）；当前预览固定为浮层卡片。</p>
              <input v-model="hotspotForm.openMode" type="text" placeholder="如 modal" />
            </label>
          </div>

          <label class="full">
            <span>metadata 扩展数据（JSON）</span>
            <p class="field-hint">可选扩展 JSON。引路方向请用上方 direction 字段，勿再在 metadata 中重复配置 guide。</p>
            <textarea v-model="hotspotForm.metadataText" rows="4" placeholder='{"customField":"value"}'></textarea>
          </label>
        </div>
        <div class="form-actions">
          <button type="button" class="btn primary" :disabled="hotspotLoading" @click="submitHotspot">
            保存
          </button>
          <button type="button" class="btn" @click="cancelHotspotForm">取消</button>
        </div>
      </div>
    </section>
  </div>
</div>
